/**
 * 设定类型定义
 *
 * 定义设定相关的TypeScript类型，供前端组件、服务和状态管理使用
 */

/**
 * 设定级别枚举
 * - global: 全文级,对整个工作区生效
 * - volume: 卷级,对指定章节范围生效
 * - chapter: 章节级,对单个章节生效
 */
export enum SettingLevel {
  GLOBAL = 'global',
  VOLUME = 'volume',
  CHAPTER = 'chapter'
}

/**
 * 设定类型枚举
 * - charter: 宪章
 * - outline: 大纲
 * - detail: 细纲
 * - summary: 摘要
 * - character: 人物
 * - supporting: 配角
 * - location: 地点
 * - item: 物品
 */
export enum SettingType {
  CHARTER = 'charter',
  OUTLINE = 'outline',
  DETAIL = 'detail',
  SUMMARY = 'summary',
  CHARACTER = 'character',
  SUPPORTING = 'supporting',
  LOCATION = 'location',
  ITEM = 'item'
}

/**
 * 设定创建请求接口
 *
 * 对应后端 SettingCreate Schema
 * 用于创建新设定的请求参数
 */
export interface SettingCreate {
  /** 设定名称,长度1-100字符 */
  name: string;
  /** 设定级别 */
  level: SettingLevel;
  /** 作用范围(可选,根据level自动生成或指定) */
  scope?: string;
  /** 设定内容(Markdown格式) */
  content: string;
  /** 设定类型 */
  setting_type: SettingType;
}

/**
 * 设定更新请求接口
 *
 * 对应后端 SettingUpdate Schema
 * 用于更新现有设定的请求参数,所有字段可选
 */
export interface SettingUpdate {
  /** 设定名称(可选) */
  name?: string;
  /** 作用范围(可选) */
  scope?: string;
  /** 设定内容(可选) */
  content?: string;
}

/**
 * 设定完整信息接口
 *
 * 对应后端 SettingResponse Schema
 * 表示设定对象的完整信息,包括系统生成的字段
 */
export interface Setting {
  /** 设定唯一标识 */
  id: number;
  /** 所属工作区ID */
  workspace_id: number;
  /** 设定名称 */
  name: string;
  /** 设定级别 */
  level: SettingLevel;
  /** 作用范围 */
  scope: string | null;
  /** 设定内容(Markdown格式) */
  content: string;
  /** 设定类型 */
  setting_type: SettingType;
  /** 用户手动锁定（防止AI修改） */
  is_locked: boolean;
  /** 创建时间(ISO 8601格式) */
  created_at: string;
  /** 更新时间(ISO 8601格式) */
  updated_at: string;
}

/**
 * 类型守卫函数,验证设定级别
 */
export function isValidSettingLevel(value: string): value is SettingLevel {
  return Object.values(SettingLevel).includes(value as SettingLevel);
}

/**
 * 类型守卫函数,验证设定类型
 */
export function isValidSettingType(value: string): value is SettingType {
  return Object.values(SettingType).includes(value as SettingType);
}